import {computed} from "vue";
import useSwr from "@/core/composables/swr/useSwr";
import {teamApi} from "@/container";
import LegacyAccess from "@/core/interfaces/Access";

/**
 * The first name behind each access point identifier.
 *
 * A proposition names collaborators by identifier, as every read tool of the
 * assistant does — the decision layer reasons on contracts and availability,
 * not on people. A diff has to be readable though, so the names are resolved
 * here, at the last moment.
 *
 * Shares the cache key of the planning screens rather than fetching again: the
 * pharmacist usually opens the assistant from the agenda, where the team is
 * already loaded.
 */
const useTeamNames = () => {
    const {data} = useSwr('access/current/team', () => teamApi().index());

    const namesByAccessId = computed<Map<number, string>>(() => {
        const names = new Map<number, string>();

        ((data.value?.team ?? []) as LegacyAccess[]).forEach(access => {
            if (access.user?.name) {
                names.set(access.id, access.user.name);
            }
        });

        return names;
    });

    /**
     * A collaborator as the diff shows them.
     *
     * Falls back to nothing rather than to an identifier: « collaborateur 412 »
     * says less than « un collaborateur » and reads like a system talking to
     * itself.
     */
    const nameOf = (accessPointId: number | null): string =>
        (accessPointId !== null ? namesByAccessId.value.get(accessPointId) : undefined) ?? 'Un collaborateur';

    return {nameOf};
};

export default useTeamNames;
